'use client'

/**
 * Avatar Component
 *
 * Image element with a fallback for representing the user.
 *
 * @see https://ui.shadcn.com/docs/components/base/avatar
 *
 * @example
 * ```tsx
 * <Avatar>
 *   <AvatarImage src="https://github.com/shadcn.png" />
 *   <AvatarFallback>CN</AvatarFallback>
 * </Avatar>
 * ```
 *
 * @remarks
 * Key Props:
 * - Avatar: `size` ('default' | 'sm' | 'xs'), `className`
 * - AvatarImage: `src`, `alt`
 * - AvatarFallback: `surface` ('muted' | 'card'), `className`
 *
 * Figma: https://www.figma.com/design/trBVcpjZslO63zxiNUI9io/?node-id=18:1398
 *
 * Intentional differences from Figma:
 * - Missing Extra Tiny (20px) size - not needed in current designs
 * - Missing Roundrect variant - only round avatars used currently
 *
 * Changelog:
 * - 2026-01-29: Removed border overlay (after:border) to match Figma (no border)
 * - 2026-01-29: Updated sizes to match Figma (default=40px, sm=32px, xs=24px)
 */

import * as React from 'react'
import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'

import { cn } from '@/utils/cn'

function Avatar({
  className,
  size = 'default',
  ...props
}: AvatarPrimitive.Root.Props & {
  size?: 'default' | 'sm' | 'xs'
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        'size-10 rounded-full data-[size=sm]:size-8 data-[size=xs]:size-6 group/avatar relative flex shrink-0 select-none',
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('rounded-full aspect-square size-full object-cover', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  surface = 'muted',
  ...props
}: AvatarPrimitive.Fallback.Props & {
  surface?: 'muted' | 'card'
}) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      data-surface={surface}
      className={cn(
        'bg-muted text-muted-foreground rounded-full flex size-full items-center justify-center text-sm group-data-[size=sm]/avatar:text-xs group-data-[size=xs]/avatar:text-xs data-[surface=card]:bg-card',
        className,
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'bg-primary text-primary-foreground ring-background absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-blend-color ring-2 select-none',
        'group-data-[size=xs]/avatar:size-2 group-data-[size=xs]/avatar:[&>svg]:hidden',
        'group-data-[size=sm]/avatar:size-2.5 group-data-[size=sm]/avatar:[&>svg]:size-2',
        'group-data-[size=default]/avatar:size-3 group-data-[size=default]/avatar:[&>svg]:size-2',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        '*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        'bg-muted text-muted-foreground size-10 rounded-full text-sm group-has-data-[size=sm]/avatar-group:size-8 group-has-data-[size=xs]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=sm]/avatar-group:[&>svg]:size-4 group-has-data-[size=xs]/avatar-group:[&>svg]:size-3 ring-background relative flex shrink-0 items-center justify-center ring-2',
        className,
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarBadge }
